import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import Modal from 'react-modal';
import styled from '@emotion/styled';

import useUserContext from '../../hooks/use-user-context';
import { createOptionMap } from '../../utilities/misc';
import { rem, hexToRgb } from '../../utilities/style';
import { convertAbsoluteToRelative } from '../../utilities/url';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import stacking from '../../theme/stacking';
import { richTextStyles } from '../../styles/type';

import TextRenderer from '../TextRenderer';

const ModalEl = styled(Modal)`
  position: relative;
  margin: auto;
  max-width: ${rem(1280)};
  padding: ${rem(20)};
  border-radius: ${rem(12)};
  background-color: ${colors.white};

  ${(props) => props.slug === 'hero-video'
  && `height: auto;
      width: 100%;
    ${media('>small')} {
      max-width: 40%;
    }
    ${media('>medium')} {
      max-width: 38%;
    }
    ${media('>large')} {
      max-width: 1100px;
      div[class*='Video'] {
        max-width: unset;
        > div[class*='Container'] {
          max-width: unset;
        }
      }
    }`
}
`;

const ModalClose = styled.button`
  position: absolute;
  top: ${rem(-12)};
  right: ${rem(-12)};
  display: block;
  width: ${rem(48)};
  height: ${rem(48)};
  z-index: 10;
  border-radius: 50%;
  padding: 0;
  border: 0;
  background: ${colors.blue['800']};
  cursor: pointer;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${rem(20)};
    height: 1px;
    background: ${colors.white};
  }

  &::before {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(45deg);
  }
`;

const Content = styled.div`
  ${richTextStyles}
  ${media('>small')} {
    height: 97%;
  }
`;

Modal.setAppElement('#___gatsby');

const Overlay = ({ slug, overlay }) => {
  const data = useStaticQuery(graphql`
    query Overlay {
      allContentfulOptionSet(filter: {name: {eq: "overlay"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { hash } = useUserContext();
  const [absoluteUrl, setAbsoluteUrl] = useState('');

  const closeModal = () => {
    navigate(
      convertAbsoluteToRelative(absoluteUrl),
      { state: { disableScroll: true } }
    );
  };

  useEffect(() => {
    // prevUrl must be obtained in front end, but not needed to close a modal.
    // Getting URL from window resolves it.
    setAbsoluteUrl(window.location.href.split('#')[0]);
  }, []);
  const options = createOptionMap(data.allContentfulOptionSet.nodes);
  const modalIsOpen = hash === `#overlay-${slug}`;

  return (
    <ModalEl
      className="topLevelModal"
      isOpen={modalIsOpen}
      onRequestClose={() => closeModal(absoluteUrl)}
      contentLabel={overlay.title}
      slug={slug}
      style={{
        overlay: {
          background: `rgba(${hexToRgb(colors.blue['800'])}, .5)`,
          zIndex: stacking.modal
        }
      }}
      shouldCloseOnOverlayClick
    >
      <ModalClose
        type="button"
        aria-label={options['overlay.close_modal_button_text']}
        onClick={closeModal}
      />
      {overlay && overlay.text && (
        <Content>
          <TextRenderer text={overlay.text} richText />
        </Content>
      )}
    </ModalEl>
  );
};

Overlay.propTypes = {
  slug: PropTypes.string,
  overlay: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.shape({
      raw: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      references: PropTypes.arrayOf(PropTypes.object)
    })
  })
};

export const query = graphql`
  fragment OverlayFragment on ContentfulOverlay {
    id
    __typename
    slug
    overlay {
      title
      text {
        raw
        references {
          ...TextRendererAssetFragment
          ...TextRendererButtonFragment
          ...TextRendererFormFragment
          ...TextRendererTrialFormFragment
          ...TextRendererImageFragment
          ...TextRendererMediaFragment
          ...TextRendererVideoFragment
          ...TextRendererWidgetFragment
        }
      }
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default Overlay;
